import { ActivatedRouteSnapshot, CanMatchFn, Router, UrlSegment } from '@angular/router';

export const SERVICES_TOP_LEVEL_TABS = new Set([
  'crop-monitoring',
  'irrigation',
  'weather-pest',
  'analysis',
  'crop-nutrition',
  'activityLogs',
]);

export interface ServicesSuffixUrl {
  plantationId: string;
  tab: string;
  query?: string;
}

/**
 * Returns the plantation id from a services URL like /services/abc-id/irrigation,
 * or null if the user is not on services or the URL has no plantation segment.
 */
export function getServicesRoutePlantationId(router: Router): string | null {
  if (!router.url.split('?')[0].startsWith('/services')) {
    return null;
  }

  let snapshot: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
  while (snapshot) {
    const id = snapshot.params?.['plantationId'];
    if (id) {
      return id;
    }
    snapshot = snapshot.firstChild;
  }

  return null;
}

/** True for GUID-shaped route segments (plantation ids, record ids, etc.). */
export function isGuidLikeSegment(segment: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
}

/**
 * Detects suffix-form services URLs: /services/{tab}/{plantationId}
 * (top-level tabs only, exactly two segments after /services).
 */
export function parseServicesSuffixUrl(url: string): ServicesSuffixUrl | null {
  const [path, query] = url.split('?');
  if (!path.startsWith('/services')) {
    return null;
  }

  const segments = path.split('/').filter(Boolean);
  if (segments[0] !== 'services' || segments.length !== 3) {
    return null;
  }

  const tab = segments[1];
  const plantationId = segments[2];

  if (!SERVICES_TOP_LEVEL_TABS.has(tab) || !isGuidLikeSegment(plantationId)) {
    return null;
  }

  return { plantationId, tab, query };
}

/** Builds canonical prefix-form URL: /services/{plantationId}/{tab} */
export function toCanonicalServicesUrl(plantationId: string, tab: string, query?: string): string {
  const path = `/services/${plantationId}/${tab}`;
  return query ? `${path}?${query}` : path;
}

/**
 * True when route segments at the ServicesComponent child level are {tab}/{guid}.
 * Used by canMatch so suffix URLs fully match before the normalize guard redirects.
 */
export function isServicesSuffixUrlSegments(segments: Pick<UrlSegment, 'path'>[]): boolean {
  if (segments.length !== 2) {
    return false;
  }

  const [tab, plantationId] = segments.map((segment) => segment.path);
  return SERVICES_TOP_LEVEL_TABS.has(tab) && isGuidLikeSegment(plantationId);
}

/** Limits the suffix catch route to legacy /services/{tab}/{plantationId} shapes only. */
export const servicesSuffixUrlCanMatch: CanMatchFn = (_route, segments) =>
  isServicesSuffixUrlSegments(segments);
